import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Cable, Menu, LogOut, LayoutDashboard, User, ArrowLeftRight } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const LOGIN_URL = () =>
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/dashboard")}`;

export function Navbar() {
  const { user, logout, setRole } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  const dashPath =
    user?.role === "candidate" ? "/candidate/dashboard"
      : user?.role === "employer" ? "/employer/dashboard"
      : (user?.role === "admin" || user?.role === "super_admin") ? "/admin/dashboard"
      : "/dashboard";

  const switchRole = async () => {
    const target = user.role === "candidate" ? "employer" : "candidate";
    setSwitching(true);
    try {
      await setRole(target);
      navigate(target === "candidate" ? "/candidate/dashboard" : "/employer/dashboard");
    } catch {
      toast.error("Could not switch role. Please try again.");
    } finally {
      setSwitching(false);
    }
  };

  const canSwitch = user && (user.role === "candidate" || user.role === "employer");

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
          <div className="w-9 h-9 rounded-lg copper-gradient flex items-center justify-center">
            <Cable className="w-5 h-5 text-white" />
          </div>
          <span className="font-display font-extrabold text-xl text-slate-900">Cable<span className="text-[#D96B27]">Careers</span></span>
        </Link>

        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-slate-600">
          <Link to="/jobs" data-testid="nav-jobs" className="hover:text-[#D96B27] transition-colors">Find Jobs</Link>
          <Link to="/employers" data-testid="nav-employers" className="hover:text-[#D96B27] transition-colors">For Employers</Link>
          <Link to="/candidates" data-testid="nav-candidates" className="hover:text-[#D96B27] transition-colors">For Candidates</Link>
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              {canSwitch && (
                <button onClick={switchRole} disabled={switching} data-testid="nav-switch-role" className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-[#D96B27] border border-slate-200 rounded-md px-3 py-1.5 disabled:opacity-50">
                  <ArrowLeftRight className="w-3.5 h-3.5" /> {switching ? "Switching…" : `Switch to ${user.role === "candidate" ? "Employer" : "Candidate"}`}
                </button>
              )}
              <button onClick={() => navigate(dashPath)} data-testid="nav-dashboard" className="flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-[#D96B27]">
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </button>
              <button onClick={logout} data-testid="nav-logout" className="flex items-center gap-1.5 text-sm font-medium px-4 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800 transition-colors">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </>
          ) : (
            <>
              <a href={LOGIN_URL()} data-testid="nav-login" className="text-sm font-medium text-slate-700 hover:text-[#D96B27]">Sign In</a>
              <a href={LOGIN_URL()} data-testid="nav-register" className="text-sm font-semibold px-4 py-2 rounded-md copper-gradient text-white hover:opacity-95 transition-opacity">Get Started</a>
            </>
          )}
        </div>

        <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          <Menu className="w-6 h-6 text-slate-800" />
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-2">
          <Link to="/jobs" onClick={() => setOpen(false)} className="block py-1.5 text-slate-700">Find Jobs</Link>
          <Link to="/employers" onClick={() => setOpen(false)} className="block py-1.5 text-slate-700">For Employers</Link>
          <Link to="/candidates" onClick={() => setOpen(false)} className="block py-1.5 text-slate-700">For Candidates</Link>
          {user ? (
            <>
              {canSwitch && (
                <button onClick={() => { setOpen(false); switchRole(); }} data-testid="nav-switch-role-mobile" className="flex items-center gap-2 py-1.5 text-slate-700"><ArrowLeftRight className="w-4 h-4" />Switch to {user.role === "candidate" ? "Employer" : "Candidate"}</button>
              )}
              <button onClick={() => { setOpen(false); navigate(dashPath); }} className="flex items-center gap-2 py-1.5 text-slate-700"><User className="w-4 h-4" />Dashboard</button>
              <button onClick={logout} className="flex items-center gap-2 py-1.5 text-red-600">Logout</button>
            </>
          ) : (
            <a href={LOGIN_URL()} className="block py-2 text-center rounded-md copper-gradient text-white font-semibold">Get Started</a>
          )}
        </div>
      )}
    </header>
  );
}

export { LOGIN_URL };
